import { apiClient } from "../util/ApiClient";
import { store } from "../store";
import {
  REIMBURSEMENT_OVERVIEW_PATH,
  REIMBURSEMENT_ITEM_PATH,
  REIMBURSEMENT_MARK_PAID_PATH,
  REIMBURSEMENT_SUBMITTERS_PATH,
  REIMBURSEMENT_SUBMITTER_PATH,
  REIMBURSEMENT_SUBMITTERS_PUBLIC_JOIN_LINK
} from "../config/Api";

import _ from "lodash";

export const REIMBURSEMENTS_OVERVIEW_LOADED = "REIMBURSEMENTS_OVERVIEW_LOADED";
export const reimbursementsOverviewLoaded = overview => ({
  type: REIMBURSEMENTS_OVERVIEW_LOADED,
  overview
});

export const REQUEST_OPEN_REIMBURSEMENT_MODAL =
  "REQUEST_OPEN_REIMBURSEMENT_MODAL";
export const requestOpenReimbursementModal = reimbursementUUID => ({
  type: REQUEST_OPEN_REIMBURSEMENT_MODAL,
  reimbursementUUID
});

export const REQUEST_CLOSE_REIMBURSEMENT_MODAL =
  "REQUEST_CLOSE_REIMBURSEMENT_MODAL";
export const requestCloseReimbursementModal = () => ({
  type: REQUEST_CLOSE_REIMBURSEMENT_MODAL
});

export const REQUEST_OPEN_PAYMENT_MODAL =
  "REQUEST_OPEN_PAYMENT_MODAL";
export const requestOpenPaymentModal = paymentUUID => ({
  type: REQUEST_OPEN_PAYMENT_MODAL,
  paymentUUID
});

export const REQUEST_CLOSE_PAYMENT_MODAL =
  "REQUEST_CLOSE_PAYMENT_MODAL";
export const requestClosePaymentModal = () => ({
  type: REQUEST_CLOSE_PAYMENT_MODAL
});

export const REIMBURSEMENT_UPDATED = "REIMBURSEMENT_UPDATED";
export const reimbursementUpdated = newReimbursement => ({
  type: REIMBURSEMENT_UPDATED,
  newReimbursement
});

export const REIMBURSEMENTS_UPDATED = "REIMBURSEMENTS_UPDATED";
export const reimbursementsUpdated = newReimbursements => ({
  type: REIMBURSEMENTS_UPDATED,
  newReimbursements
});

export const PAYMENT_ADDED = "PAYMENT_ADDED";
export const paymentAdded = newPayment => ({
  type: PAYMENT_ADDED,
  payment: newPayment
})

export const SUBMITTER_ADDED = "SUBMITTER_ADDED";
export const submitterAdded = newSubmitter => ({
  type: SUBMITTER_ADDED,
  payload: newSubmitter
});

export const SUBMITTER_REMOVED = "SUBMITTER_REMOVED";
export const submitterRemoved = submitterUUID => ({
  type: SUBMITTER_REMOVED,
  payload: submitterUUID
});

export const PUBLIC_JOIN_LINK_CHANGED = "PUBLIC_JOIN_LINK_CHANGED";
export const publicJoinLinkChanged = newLinkData => ({
  type: PUBLIC_JOIN_LINK_CHANGED,
  payload: newLinkData
});

const handleUpdatedReimbursements = (oldState, newReimbursements) => {
  const keyedReimbursements = _.keyBy(newReimbursements, r => r.uuid);
  const newItems = oldState.items.map(reimbursement => {
    if (keyedReimbursements[reimbursement.uuid] === undefined) {
      return reimbursement;
    }
    return keyedReimbursements[reimbursement.uuid];
  });

  return {
    ...oldState,
    items: newItems
  };
};

const reimbursementsInitialState = {
  submitters: [],
  items: [],
  payments: [],
  public_join_link: {},
  openedReimbursementModal: null,
  openedPaymentModal: null,
};
export const reimbursementsReducer = (
  state = reimbursementsInitialState,
  action
) => {
  switch (action.type) {
    case REIMBURSEMENTS_OVERVIEW_LOADED:
      return {
        ...state,
        submitters: action.overview.submitters,
        items: action.overview.items,
        payments: action.overview.payments,
        public_join_link: action.overview.public_join_link
      };
    case REQUEST_OPEN_REIMBURSEMENT_MODAL:
      return {
        ...state,
        openedReimbursementModal: action.reimbursementUUID
      };
    case REQUEST_CLOSE_REIMBURSEMENT_MODAL:
      return {
        ...state,
        openedReimbursementModal: null
      };
    case REQUEST_OPEN_PAYMENT_MODAL:
      return {
        ...state,
        openedPaymentModal: action.paymentUUID
      };
    case REQUEST_CLOSE_PAYMENT_MODAL:
      return {
        ...state,
        openedPaymentModal: null
      };
    case REIMBURSEMENT_UPDATED:
      return handleUpdatedReimbursements(state, [action.newReimbursement]);
    case REIMBURSEMENTS_UPDATED:
      return handleUpdatedReimbursements(state, action.newReimbursements);
    case PAYMENT_ADDED:
      return {
        ...state,
        payments: [action.payment].concat(state.payments)
      }
    case SUBMITTER_ADDED:
      const newSubmitters = _.clone(state.submitters);
      newSubmitters.push(action.payload);
      return {
        ...state,
        submitters: newSubmitters
      };
    case SUBMITTER_REMOVED:
      return {
        ...state,
        submitters: state.submitters.filter(
          submitter => submitter.uuid !== action.payload
        ),
        items: state.items.filter(
          reimbursement => reimbursement.submitter.uuid !== action.payload
        )
      };
    case PUBLIC_JOIN_LINK_CHANGED:
      return {
        ...state,
        public_join_link: action.payload
      };
    default:
      return state;
  }
};

export const loadReimbursementsOverview = () => {
  return apiClient()
    .get(REIMBURSEMENT_OVERVIEW_PATH)
    .then(response => response.data);
};

export const performReimbursementAction = (reimbursement, action) => {
  return apiClient()
    .post(REIMBURSEMENT_ITEM_PATH(reimbursement.uuid), {
      action
    })
    .then(response => {
      const newReimbursement = response.data;
      store.dispatch(reimbursementUpdated(newReimbursement));
      return newReimbursement;
    });
};

export const payReimbursements = (reimbursements, options) => {
  return apiClient()
    .post(REIMBURSEMENT_MARK_PAID_PATH, {
      reimbursements: reimbursements.map(r => r.uuid),
      method: options.method,
      check_number: options.check_number,
    })
    .then(response => {
      const newReimbursements = response.data.updated;
      store.dispatch(reimbursementsUpdated(newReimbursements));
      store.dispatch(paymentAdded(response.data.payment));
      store.dispatch(requestOpenPaymentModal(response.data.payment.uuid));
      return newReimbursements;
    });
};

export const addSubmitter = email => {
  return apiClient()
    .post(REIMBURSEMENT_SUBMITTERS_PATH, {
      email: email
    })
    .then(response => {
      return response.data;
    });
};

export const deleteSubmitter = submitterUUID => {
  return apiClient().delete(REIMBURSEMENT_SUBMITTER_PATH(submitterUUID));
};

export const changePublicJoinLink = enable => {
  return apiClient()
    .post(REIMBURSEMENT_SUBMITTERS_PUBLIC_JOIN_LINK, {
      enable
    })
    .then(response => {
      return response.data;
    });
};



// WEBPACK FOOTER //
// ./frontend/src/actions/reimbursements.js